<template>
  <div class="product-page">
    <LoadingSpinner :isLoading="isLoading" />

    <div class="product-list-section">
      <div class="header">
        <h2 class="brand-title">
          {{ t('productList.forBrand', { name: selectedTitle }) }}
        </h2>

        <div class="sort-options">
          <div class="custom-select-wrapper">
            <div class="custom-select">
              <select id="sort" v-model="sortOption">
                <option id="filtrar" value="">{{ t('productList.filter') }}</option>
                <option value="valor_venda:asc">{{ t('productList.priceAsc') }}</option>
                <option value="valor_venda:desc">{{ t('productList.priceDesc') }}</option>
                <option value="nome:asc">{{ t('productList.nameAsc') }}</option>
                <option value="nome:desc">{{ t('productList.nameDesc') }}</option>
              </select>
              <svg v-if="!sortOption" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                stroke-width="1.5" stroke="currentColor" class="icon">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M4.5 5.25l7.5 7.5 7.5-7.5m-15 6l7.5 7.5 7.5-7.5" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <template v-if="isLoading">
        <ProdutosSkeleton />
      </template>

      <template v-else-if="productsLoaded && sortedProducts.length === 0">
        <p>{{ t('productList.noBrandResults') }}</p>
      </template>

      <template v-else>
        <div class="product-list">
          <productCard v-for="(product, index) in sortedProducts" :key="index" :product="product"
            :current-page="currentPage.value" @product-click="saveCurrentPage" />
        </div>
      </template>

      <div class="pagination-container" v-if="totalPages > 1">
        <button @click="prevPage" :disabled="currentPage === 1" class="pagination-button">
          {{ t('productList.previous') }}
        </button>

        <div class="pagination-numbers">
          <button v-for="page in visiblePages" :key="page" @click="goToPage(page)"
            :class="{ active: page === currentPage }">
            {{ page }}
          </button>

          <span v-if="totalPages > visiblePages[visiblePages.length - 1]">...</span>
          <button v-if="currentPage < totalPages - 4" @click="goToPage(totalPages)">
            {{ totalPages }}
          </button>
        </div>

        <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-button">
          {{ t('productList.next') }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LoadingSpinner from '@/components/template/loading-spinner.vue';
import {
  fetchBrands,
  fetchProductsByBrandId,
  fetchCategoriesByBrandId
} from '@/services/apiService';
import productCard from '@/components/produtos/productCard.vue';
import { useI18n } from 'vue-i18n';
import ProdutosSkeleton from '@/components/produtos/ProdutosSkeleton.vue';

const { t } = useI18n();

const filteredProducts = ref([]);
const categories = ref([]);
const selectedTitle = ref('Produtos');
const sortOption = ref('');
const isLoading = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const totalProducts = ref(0);
const route = useRoute();
const router = useRouter();
const brandId = ref(null);
const selectedCategory = ref(null);
const productsLoaded = ref(false);


const saveCurrentPage = () => {
  sessionStorage.setItem('currentPage', currentPage.value);
};

const fetchCategories = async () => {
  try {
    if (!brandId.value) return;
    const data = await fetchCategoriesByBrandId(brandId.value);
    categories.value = data;
  } catch (error) {
    console.error('Erro ao buscar categorias:', error);
  }
};

const fetchBrandBySlug = async (brandSlug) => {
  try {
    const brands = await fetchBrands();
    const brand = brands.find(b => b.slug === brandSlug);
    return brand ? { id: brand.id, name: brand.nome } : { id: null, name: '' };
  } catch (error) {
    console.error('Erro ao buscar marcas:', error);
    return { id: null, name: '' };
  }
};

const sortedProducts = computed(() => {
  if (!sortOption.value || !filteredProducts.value.length) return filteredProducts.value;

  const [orderBy, order] = sortOption.value.split(':');
  const sorted = [...filteredProducts.value];

  if (orderBy === 'valor_venda') {
    sorted.sort((a, b) => {
      const priceA = parseFloat(a.valor_venda);
      const priceB = parseFloat(b.valor_venda);
      return order === 'asc' ? priceA - priceB : priceB - priceA;
    });
  } else if (orderBy === 'nome') {
    sorted.sort((a, b) => {
      return order === 'asc'
        ? a.nome.localeCompare(b.nome)
        : b.nome.localeCompare(a.nome);
    });
  }

  return sorted;
});

const fetchProducts = async (page = 1) => {
  currentPage.value = page;
  isLoading.value = true;
  productsLoaded.value = false; // <- começa falso

  window.scrollTo(0, 0);

  if (!brandId.value) {
    isLoading.value = false;
    return;
  }

  try {
    const [orderBy, order] = sortOption.value.split(':');

    const params = {
      page: currentPage.value,
      order_by: orderBy || 'tem_estoque,id',
      order: order || 'desc',
      category_id: selectedCategory.value ? selectedCategory.value.id : '',
    };

    const response = await fetchProductsByBrandId(brandId.value, params);

    const products = response.data || [];

    const inStock = products.filter(p => p.tem_estoque);
    const outOfStock = products.filter(p => !p.tem_estoque);
    filteredProducts.value = [...inStock, ...outOfStock];

    totalProducts.value = response.total || 0;
    totalPages.value = response.last_page || Math.ceil(totalProducts.value / 20);
    currentPage.value = response.current_page || page;
  } catch (err) {
    console.error('Erro ao buscar produtos:', err);
  } finally {
    isLoading.value = false;
    productsLoaded.value = true; // <- agora sim carregou
  }
};



const goToPage = (page) => {
  if (page !== currentPage.value) {
    currentPage.value = page;
    router.push({
      name: 'marca',
      params: {
        brandSlug: route.params.brandSlug,
        categoriaSlug: route.params.categoriaSlug
      },
      query: {
        ...route.query,
        page
      }
    });
    fetchProducts(page);
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    goToPage(currentPage.value + 1);
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    goToPage(currentPage.value - 1);
  }
};

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const start = Math.max(1, current - 2);
  const end = Math.min(total, start + 4);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

watch(
  [
    () => route.params.brandSlug,
    () => route.params.categoriaSlug,
    () => route.query.page,
    () => sortOption.value
  ],
  async ([newBrand, newCat, newPage, newSort], [oldBrand, oldCat, oldPage, oldSort]) => {
    if (newBrand !== oldBrand) {
      const brand = await fetchBrandBySlug(newBrand);
      brandId.value = brand.id;
      selectedTitle.value = brand.name || 'Produtos';
      await fetchCategories();
    }

    if (newCat !== oldCat && categories.value.length) {
      selectedCategory.value = categories.value.find(c => c.slug === newCat) || null;
    }

    if (newPage !== oldPage) {
      currentPage.value = parseInt(newPage) || 1;
      sessionStorage.setItem('currentPage', currentPage.value);
      await fetchProducts(currentPage.value);
    }

    if (newSort !== oldSort) {
      await fetchProducts(currentPage.value);
    }
  }
);

onMounted(async () => {
  currentPage.value = route.query.page
    ? parseInt(sessionStorage.getItem('currentPage')) || 1
    : 1;

  const brand = await fetchBrandBySlug(route.params.brandSlug);
  brandId.value = brand.id;
  selectedTitle.value = brand.name || 'Produtos';

  await fetchCategories();

  if (route.params.categoriaSlug) {
    const category = categories.value.find(c => c.slug === route.params.categoriaSlug);
    if (category) {
      selectedCategory.value = category;
    }
  }

  if (route.query.page) {
    sessionStorage.setItem('currentPage', currentPage.value);
  }

  await fetchProducts(currentPage.value);
});
</script>



<style scoped>
html,
body {
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;

}

.categories-container {
  margin-bottom: 20px;
}

.brand-title {
  font-family: 'Oswald', sans-serif;
}

.category-list {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  padding: 0;
}

.category-item {
  margin-right: 15px;

}

.category-link {
  padding: 10px 15px;
  background-color: #f0f0f0;
  border-radius: 8px;
  text-decoration: none;
  color: #333;
  transition: background-color 0.3s ease;
  font-family: 'Oswald', sans-serif;
}

.category-link:hover {
  background-color: gray;
  color: white;
}

.container {
  max-width: 1500px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

.product-page {
  display: flex;
  padding: 20px;
  font-family: 'Roboto', sans-serif;
  max-width: 1500px;
  margin: 0 auto;
  font-family: 'Oswald', sans-serif;
}

.category-sidebar {
  width: 280px;
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  position: relative;
}

.category-list {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
}

.category-item {
  position: relative;
}

.category-link {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  color: #333;
  text-decoration: none;
  border-radius: 8px;
  margin-bottom: 12px;
  background: #ffffff;
  border: 1px solid transparent;
  transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  font-weight: 600;
}

.category-link:hover,
.category-link.active {
  background-color: gray;
  color: #ffffff;
  border-color: gray;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.category-link i {
  transition: transform 0.3s ease;
}

.department-list {
  list-style: none;
  padding: 0;
  margin: 0;
  position: absolute;
  left: 100%;
  top: 0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  width: 200px;
  display: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.category-item:hover .department-list {
  display: block;
  opacity: 1;
}

.department-link {
  display: block;
  padding: 10px 16px;
  color: #333;
  text-decoration: none;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.3s ease;
}

.department-link:hover {
  background: #f5f5f5;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.product-list-section {
  flex: 1;
  padding: 20px;
  max-width: 100vw;
  box-sizing: border-box;
}


.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.product-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
}





@media (max-width: 768px) {
  .product-list {
    flex-direction: column;
    align-items: center;
  }

  .product-item {
    width: 100%;
    max-width: 300px;
    margin-bottom: 20px;
  }
}

@media (min-width: 769px) and (max-width: 1200px) {
  .product-item {
    width: 45%;
    max-width: 260px;
  }
}

@media (min-width: 1201px) {
  .product-item {
    width: 22%;
    max-width: 260px;
  }
}

.linha-abaixo-titulo {
  height: 4px;
  background-color: #e0e0e0;
  border-radius: 2px;
  width: 100%;
  margin-bottom: 20px;
}

h2 {
  font-size: 2rem;
  color: gray;
  font-weight: bold;
  font-family: 'Lato', sans-serif;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  color: #fff;
}

.pagination-button {
  background-color: #E23B33;
  color: #fff;
  border: none;
  padding: 8px 16px;
  margin: 0 5px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.3s;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-button:hover:not(:disabled) {
  background-color: #ff3333;
}

.pagination-numbers {
  display: flex;
  gap: 5px;
  margin: 0 10px;
}

.pagination-numbers button {
  background-color: #fff;
  color: #E23B33;
  border: 2px solid #E23B33;
  padding: 4px 8px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;
}

.pagination-numbers button.active {
  background-color: #E23B33;
  color: #fff;
}

.pagination-numbers button:hover:not(.active) {
  background-color: #E23B33;
  color: #fff;
}

@media (max-width: 768px) {
  .pagination-numbers {
    display: none;
  }
}


@media (max-width: 768px) {
  .pagination-numbers {
    display: none;
  }
}


.out-of-stock-tag {
  color: red;
  font-weight: bold;
  background-color: #f8d7da;
  padding: 3px 6px;
  border-radius: 3px;
  display: inline-block;
  margin-top: 5px;
}


@media (max-width: 768px) {
  .category-sidebar {
    display: none;
  }

  .product-page {
    padding: 1rem;
  }

  .pagination-controls {
    flex-direction: column;
  }

  .pagination-controls button {
    min-width: auto;

  }

  .pagination-numbers {
    display: none;
  }

  .pagination-button {
    font-size: 14px;
    padding: 8px 16px;
  }
}


.sort-options select {
  padding: 4px 10px;
  font-size: 16px;
  border-radius: 10px;
  border: 1px solid red;
  background-color: #f0f0f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  appearance: none;
  cursor: pointer;
  transition: border-color 0.3s, box-shadow 0.3s;
  color: #827F7F
}

.custom-select-wrapper {
  position: relative;
  margin-right: 90px;
}

.custom-select select:focus {
  outline: none;
  border: 1px solid red;
  /* Mantém a borda vermelha */
}

.custom-select {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.custom-select select {
  text-align: left;
  line-height: 30px;
  padding: 0 10px;
}


.custom-select .icon {
  position: absolute;
  right: 15px;
  pointer-events: none;
  width: 16px;
  height: 16px;
}

.custom-select option#filtrar {
  text-align: left;
  padding-left: 10px;
}

.sort-options select:hover,
.sort-options select:focus {
  border-color: #5f5f5f;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}



@media (max-width: 768px) {
  .category-sidebar {
    display: none;
  }

  .product-page {
    padding: 1rem;
  }

  .pagination-controls {
    flex-direction: column;
  }

  .pagination-controls button {
    min-width: auto;

  }
}


.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-left-color: gray;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .pagination-numbers {
    display: none;
  }
}

.sort-options select {
  padding: 4px 10px;
  font-size: 16px;
  border-radius: 5px;
  background-color: #f0f0f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 0 0 0.5px red;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.3s, box-shadow 0.3s;
  color: #827F7F;
  border: none;

}

.sort-select {
  margin-right: 20px;
  /* Ajuste o valor conforme necessário */
}

.sort-options select:hover,
.sort-options select:focus {
  border-color: #5f5f5f;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.category-link i {
  transition: transform 0.3s ease;
}

.product-list-section {
  flex: 1;
  padding: 20px;
  max-width: 100vw;
  box-sizing: border-box;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}



.out-of-stock-tag {
  color: red;
  font-weight: bold;
  background-color: #f8d7da;
  padding: 3px 6px;
  border-radius: 3px;
  display: inline-block;
  margin-top: 5px;
}

.product-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
}

/* Remova ou ajuste as media queries existentes para o product-item */
@media (max-width: 768px) {
  .product-list {
    grid-template-columns: repeat(2, 1fr);
    /* Garante 2 produtos por linha em telas pequenas */
  }
}

/* Mantenha os outros estilos conforme necessário para telas maiores */
@media (min-width: 769px) and (max-width: 1200px) {
  .product-list {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
}

@media (min-width: 1201px) {
  .product-list {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
}

@media (max-width: 768px) {


  .product-item {
    width: 100%;
    max-width: 300px;
    margin-bottom: 20px;

  }

  .category-sidebar {
    display: none;
  }
}

@media (min-width: 769px) and (max-width: 1200px) {
  .product-item {
    width: 45%;
    max-width: 260px;

  }


}

@media (min-width: 1201px) {
  .product-item {
    width: 22%;
    max-width: 260px;
  }
}

@media (max-width: 900px) {
  .sort-select {
    margin-right: 0;
  }
}
</style>
